import { useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { checkStaff, setupStaff, changeStaff } from '@generated/server/staff';
import { getStaffPass, setStaffPass } from '@generated/hooks/staffPass';

type Mode = 'signin' | 'setup' | 'change';

export function StaffLogin({ staff, onChange }: { staff: boolean; onChange: (s: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('signin');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const openSignIn = async () => {
    setOpen(true); setMode('signin'); setError(null); setPass('');
    try { const r = await checkStaff({ data: {} }); if (r?.needsSetup) setMode('setup'); } catch (e) { console.error(e); }
  };
  const openChange = () => { setOpen(true); setMode('change'); setError(null); setPass(''); };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (mode === 'change') {
        if (pass.length < 6) { setError('Use at least 6 characters.'); return; }
        const r = await changeStaff({ data: { oldPass: getStaffPass(), newPass: pass } });
        if (!r?.ok) { setError('Could not change the passcode. Sign out, sign in again, and retry.'); return; }
        setStaffPass(pass); setOpen(false); setNotice('Passcode changed.'); setTimeout(() => setNotice(null), 4000);
        return;
      }
      if (mode === 'setup') {
        if (pass.length < 6) { setError('Use at least 6 characters.'); return; }
        const s = await setupStaff({ data: { pass } });
        if (!s?.ok) { setError('A passcode is already set. Enter it to sign in.'); setMode('signin'); return; }
      }
      const r = await checkStaff({ data: { pass } });
      if (r?.staff) { setStaffPass(pass); onChange(true); setOpen(false); }
      else setError('That passcode is not correct.');
    } catch (e) { console.error(e); setError('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const title = mode === 'setup' ? 'Create your staff passcode' : mode === 'change' ? 'Change staff passcode' : 'Staff sign in';
  const desc = mode === 'setup' ? 'Choose a passcode (6+ characters). You\'ll use it to see orders and the Views & Sales report.'
    : mode === 'change' ? 'Enter a new passcode (6+ characters).' : 'Enter the staff passcode.';

  return (
    <>
      {staff ? (
        <span className="inline-flex flex-wrap items-center justify-end gap-3 text-xs text-muted-foreground">
          {notice && <span className="text-primary">{notice}</span>}
          <button onClick={openChange} className="underline underline-offset-2 hover:text-foreground">Change passcode</button>
          <button onClick={() => { setStaffPass(null); onChange(false); }} className="underline underline-offset-2 hover:text-foreground">Staff sign out</button>
        </span>
      ) : (
        <button onClick={openSignIn} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Lock className="h-3 w-3" />Staff sign in</button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-[family-name:var(--font-heading)]">{title}</DialogTitle>
            <DialogDescription>{desc}</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-3">
            <Input type="password" aria-label={mode === 'change' ? 'New passcode' : 'Passcode'} value={pass} onChange={(e) => setPass(e.target.value)} className="h-11 rounded-none" />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button type="submit" disabled={busy || !pass} className="inline-flex min-h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{mode === 'setup' ? 'Save passcode & sign in' : mode === 'change' ? 'Save new passcode' : 'Sign in'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
